import React, { useState, useRef, useCallback, useEffect } from 'react';
import Sidebar from './Sidebar';
import Toolbar from './Toolbar';
import CanvasStage from './CanvasStage';
import useAnnotationHistory from './useAnnotationHistory';
import { Undo2, Redo2, Download, Save, X, Maximize2, Minimize2 } from 'lucide-react';

const DEFAULT_STYLE = {
  stroke: '#ef4444',
  fill: 'transparent',
  strokeWidth: 4,
};

const TEMPLATES = [
  {
    label: 'Fissure',
    annotations: [
      { type: 'line', points: [80, 200, 200, 100], stroke: '#ef4444', strokeWidth: 3 },
      { type: 'text', x: 90, y: 80, text: 'Fissure', fontSize: 16, color: '#ef4444', stroke: '#ef4444' },
    ],
  },
  {
    label: 'Infiltration',
    annotations: [
      { type: 'ellipse', x: 150, y: 150, radiusX: 60, radiusY: 40, stroke: '#3b82f6', strokeWidth: 3, fill: 'rgba(59,130,246,0.15)' },
      { type: 'text', x: 110, y: 105, text: 'Infiltration', fontSize: 16, color: '#3b82f6', stroke: '#3b82f6' },
    ],
  },
  {
    label: 'Joint dégradé',
    annotations: [
      { type: 'rect', x: 80, y: 120, width: 160, height: 30, stroke: '#f97316', strokeWidth: 3, fill: 'rgba(249,115,22,0.1)' },
      { type: 'text', x: 90, y: 100, text: 'Joint dégradé', fontSize: 14, color: '#f97316', stroke: '#f97316' },
    ],
  },
];

let _templateUid = 0;

export default function Annotator({ imageSrc, onSave, onClose, initialAnnotations = [] }) {
  const stageRef = useRef(null);
  const { annotations, setAnnotations: pushAnnotations, undo, redo, canUndo, canRedo } = useAnnotationHistory(initialAnnotations);
  const [activeMode, setActiveMode] = useState('annotate'); // crop | adjust | annotate | sticker
  const [activeTool, setActiveTool] = useState('select');
  const [style, setStyle] = useState(DEFAULT_STYLE);
  const [selectedId, setSelectedId] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [currentImageSrc, setCurrentImageSrc] = useState(imageSrc);
  const [pendingSticker, setPendingSticker] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [autoNumber, setAutoNumber] = useState(1);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') { e.preventDefault(); undo(); }
      if ((e.ctrlKey || e.metaKey) && e.key === 'y') { e.preventDefault(); redo(); }
      if ((e.ctrlKey || e.metaKey) && e.key === 's') { e.preventDefault(); handleSave(); }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedId) {
          pushAnnotations((prev) => prev.filter((a) => a.id !== selectedId));
          setSelectedId(null);
        }
      }
      if (e.key === 'Escape') {
        if (selectedId) setSelectedId(null);
        else if (onClose) onClose();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  // Listen for crop result
  useEffect(() => {
    const handler = (e) => {
      if (e.detail) {
        setCurrentImageSrc(e.detail);
        setActiveMode('annotate');
      }
    };
    window.addEventListener('annotator-crop', handler);
    return () => window.removeEventListener('annotator-crop', handler);
  }, []);

  const handleModeChange = useCallback((mode) => {
    setActiveMode(mode);
    if (mode === 'annotate') setActiveTool('select');
    if (mode === 'crop') setActiveTool('select');
  }, []);

  const handleToolChange = useCallback((tool) => {
    setActiveTool(tool);
    if (activeMode !== 'annotate') setActiveMode('annotate');
  }, [activeMode]);

  const handleStickerSelect = useCallback((sticker) => {
    setPendingSticker(sticker);
  }, []);

  const handleStickerPlaced = useCallback(() => {
    setPendingSticker(null);
  }, []);

  const handleAddNumber = useCallback(() => {
    const num = autoNumber;
    setAutoNumber((n) => n + 1);
    pushAnnotations((prev) => [
      ...prev,
      {
        id: `num_${Date.now()}_${num}`,
        type: 'text',
        x: 100 + (num * 20) % 200,
        y: 100 + (num * 15) % 150,
        text: `(${num})`,
        fontSize: 22,
        color: '#ef4444',
        stroke: '#ef4444',
      },
    ]);
  }, [autoNumber, pushAnnotations]);

  const handleApplyTemplate = useCallback((template) => {
    const newAnns = template.annotations.map((a) => ({
      ...a,
      id: `tpl_${Date.now()}_${++_templateUid}`,
    }));
    pushAnnotations((prev) => [...prev, ...newAnns]);
    setShowTemplates(false);
  }, [pushAnnotations]);

  const handleSave = useCallback(() => {
    if (!stageRef.current) return;
    const dataUrl = stageRef.current.toDataURL({ pixelRatio: 2 });
    onSave?.({ dataUrl, annotations });
  }, [annotations, onSave]);

  const handleDownload = useCallback(() => {
    if (!stageRef.current) return;
    const dataUrl = stageRef.current.toDataURL({ pixelRatio: 2 });
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `annotation_${Date.now()}.png`;
    a.click();
  }, []);

  const containerClass = isFullscreen
    ? 'fixed inset-0 z-[9999] flex flex-col bg-gray-950'
    : 'flex flex-col w-full h-full bg-gray-950 rounded-xl overflow-hidden';

  return (
    <div className={containerClass}>
      {/* Top bar */}
      <div className="flex items-center justify-between bg-gray-900 px-3 py-1.5 shrink-0 border-b border-gray-800">
        <div className="flex items-center gap-1">
          <button onClick={undo} disabled={!canUndo}
            className="p-1.5 rounded hover:bg-gray-700 disabled:opacity-30 transition-colors" title="Annuler (Ctrl+Z)">
            <Undo2 size={16} />
          </button>
          <button onClick={redo} disabled={!canRedo}
            className="p-1.5 rounded hover:bg-gray-700 disabled:opacity-30 transition-colors" title="Rétablir (Ctrl+Y)">
            <Redo2 size={16} />
          </button>

          <div className="w-px h-5 bg-gray-700 mx-1" />

          {/* Auto-number */}
          <button onClick={handleAddNumber}
            className="p-1.5 rounded hover:bg-gray-700 text-xs font-bold text-yellow-400" title="Ajouter numéro">
            ({autoNumber})
          </button>

          {/* Templates */}
          <div className="relative">
            <button onClick={() => setShowTemplates((v) => !v)}
              className="p-1.5 rounded hover:bg-gray-700 text-xs text-gray-300" title="Templates">
              📋 Templates
            </button>
            {showTemplates && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowTemplates(false)} />
                <div className="absolute top-full mt-1 left-0 bg-gray-800 rounded-lg shadow-xl p-2 z-50 w-44">
                  {TEMPLATES.map((t, i) => (
                    <button
                      key={i}
                      onClick={() => handleApplyTemplate(t)}
                      className="w-full text-left px-3 py-2 rounded hover:bg-gray-700 text-sm text-gray-200"
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1">
          <button onClick={handleDownload}
            className="p-1.5 rounded hover:bg-gray-700 transition-colors" title="Télécharger">
            <Download size={16} />
          </button>
          <button onClick={handleSave}
            className="px-3 py-1 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium flex items-center gap-1 transition-colors"
            title="Enregistrer (Ctrl+S)"
          >
            <Save size={14} /> Enregistrer
          </button>
          <button onClick={() => setIsFullscreen((v) => !v)}
            className="p-1.5 rounded hover:bg-gray-700 transition-colors" title="Plein écran">
            {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </button>
          {onClose && (
            <button onClick={onClose}
              className="p-1.5 rounded hover:bg-red-600/80 transition-colors" title="Fermer">
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Main area */}
      <div className="flex flex-1 min-h-0">
        {/* Sidebar */}
        <Sidebar
          activeMode={activeMode}
          onModeChange={handleModeChange}
          onStickerSelect={handleStickerSelect}
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed((v) => !v)}
        />

        {/* Canvas */}
        <CanvasStage
          imageSrc={currentImageSrc}
          annotations={annotations}
          onAnnotationsChange={pushAnnotations}
          activeTool={activeTool}
          activeMode={activeMode}
          style={style}
          selectedId={selectedId}
          onSelectedChange={setSelectedId}
          stageRef={stageRef}
          pendingSticker={pendingSticker}
          onStickerPlaced={handleStickerPlaced}
        />
      </div>

      {/* Bottom toolbar */}
      {activeMode === 'annotate' && (
        <Toolbar
          activeTool={activeTool}
          onToolChange={handleToolChange}
          style={style}
          onStyleChange={setStyle}
        />
      )}

      {/* Legend overlay (annotation count) */}
      {annotations.length > 0 && (
        <div className="absolute bottom-14 right-3 bg-gray-800/80 backdrop-blur rounded-lg px-2.5 py-1.5 text-[10px] text-gray-400 z-30 pointer-events-none">
          {annotations.length} annotation{annotations.length > 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
}
